import { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import Markdown from '../Markdown'
import { REDUCED_MOTION, bodyText, colours } from '../player/playerStyles'
import Feedback from './Feedback'
import useBlockState, { shuffle } from './useBlockState'

const sameOrder = (a, b) => a.length === b.length && a.every((id, index) => id === b[index])

// Items are stored in the correct order, so the starting order is shuffled
// until it differs from it.
const startingOrder = (correct) => {
  let order = shuffle(correct)
  for (let tries = 0; tries < 20 && sameOrder(order, correct); tries += 1) order = shuffle(correct)
  return sameOrder(order, correct) ? [...correct].reverse() : order
}

const Row = ({ item, position, count, onMove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id })
  return (
    <Box
      component="li"
      ref={setNodeRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        mb: 1,
        p: 0.5,
        border: `1px solid ${colours.border}`,
        borderRadius: 1.5,
        bgcolor: '#FFFFFF',
        position: 'relative',
        zIndex: isDragging ? 1 : 'auto',
        boxShadow: isDragging ? '0 4px 12px rgba(36, 47, 64, 0.25)' : 'none',
        transform: CSS.Transform.toString(transform),
        transition,
        [REDUCED_MOTION]: { transition: 'none' },
      }}
    >
      <IconButton
        {...attributes}
        {...listeners}
        aria-label={`Drag '${item.text}' to reorder`}
        sx={{ cursor: 'grab', touchAction: 'none', color: colours.muted, display: { xs: 'none', sm: 'inline-flex' } }}
      >
        <DragIndicatorIcon />
      </IconButton>
      <Typography sx={{ flex: 1, minWidth: 0, fontSize: '1.0625rem', lineHeight: 1.5, pl: { xs: 1, sm: 0 }, overflowWrap: 'anywhere' }}>
        <Box component="span" sx={{ fontWeight: 700 }}>
          {position + 1}.{' '}
        </Box>
        {item.text}
      </Typography>
      <IconButton onClick={() => onMove(position, -1)} disabled={position === 0} aria-label={`Move '${item.text}' up`}>
        <KeyboardArrowUpIcon />
      </IconButton>
      <IconButton
        onClick={() => onMove(position, 1)}
        disabled={position === count - 1}
        aria-label={`Move '${item.text}' down`}
      >
        <KeyboardArrowDownIcon />
      </IconButton>
    </Box>
  )
}

const Sequence = ({ block, initialState, onStateChange }) => {
  const correct = block.items.map((item) => item.id)
  const [state, update] = useBlockState(
    () => {
      const saved = initialState?.order ?? []
      const valid = saved.length === correct.length && correct.every((id) => saved.includes(id))
      return { attempts: 0, solved: false, ...initialState, order: valid ? saved : startingOrder(correct) }
    },
    onStateChange,
  )
  // 'correct' | 'incorrect' | 'shown' | null (not checked since the last move)
  const [result, setResult] = useState(state.solved ? 'correct' : null)
  const [moved, setMoved] = useState('')
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const failedAttempts = state.attempts - (state.solved ? 1 : 0)

  const reorder = (from, to) => {
    if (to < 0 || to >= state.order.length || from === to) return
    const text = block.items.find((item) => item.id === state.order[from])?.text
    update({ ...state, order: arrayMove(state.order, from, to) })
    setResult(null)
    setMoved(`'${text}' moved to position ${to + 1} of ${state.order.length}.`)
  }

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return
    reorder(state.order.indexOf(active.id), state.order.indexOf(over.id))
  }

  const check = () => {
    const solved = sameOrder(state.order, correct)
    update({ ...state, attempts: state.attempts + 1, solved: state.solved || solved })
    setResult(solved ? 'correct' : 'incorrect')
    setMoved('')
  }

  const showAnswer = () => {
    update({ ...state, order: correct })
    setResult('shown')
    setMoved('')
  }

  return (
    <Box sx={{ maxWidth: '74ch' }}>
      <Markdown sx={{ ...bodyText, mb: 1 }}>{block.prompt}</Markdown>
      <Typography sx={{ fontSize: '1rem', lineHeight: 1.6, color: colours.muted, mb: 2 }}>
        Use the up and down buttons on each row to change the order.
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}> You can also drag a row by its handle.</Box>
      </Typography>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={state.order} strategy={verticalListSortingStrategy}>
          <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {state.order.map((id, position) => (
              <Row
                key={id}
                item={block.items.find((item) => item.id === id)}
                position={position}
                count={state.order.length}
                onMove={(from, direction) => reorder(from, from + direction)}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>

      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5} sx={{ mt: 2 }}>
        <Button variant="contained" onClick={check}>
          Check order
        </Button>
        {failedAttempts >= 2 && result !== 'correct' && result !== 'shown' && (
          <Button variant="outlined" onClick={showAnswer}>
            Show the correct order
          </Button>
        )}
      </Stack>

      <Box aria-live="polite" sx={{ mt: 2 }}>
        {result === 'correct' && (
          <Feedback tone="good" label="Correct order">
            {block.feedback.correct}
          </Feedback>
        )}
        {result === 'incorrect' && (
          <Feedback tone="bad" label="Not the right order yet">
            {block.feedback.incorrect}
          </Feedback>
        )}
        {result === 'shown' && (
          <Feedback tone="info" label="This is the correct order">
            {block.feedback.correct}
          </Feedback>
        )}
        {!result && moved && <Typography sx={{ fontSize: '1rem', color: colours.muted }}>{moved}</Typography>}
      </Box>
    </Box>
  )
}

export default Sequence
